/**
 * Agent Activity preview harness (QA / design aid).
 *
 * Locally the read-only agent only exercises a couple of tool calls, so most of
 * the timeline's states (subagents, grouped ×N, thinking, redacted reasoning,
 * denied/skipped, errored tools, truncated results, turn errors, the live
 * spinner) never appear naturally. This pushes hand-built messages straight into
 * the chat store so the real `AgentActivity` component renders every state in
 * its real context.
 *
 * Gated OFF by default via {@link isAgentActivityDemoEnabled}; nothing renders
 * unless it's explicitly switched on, so it's safe to keep in-tree for future
 * use. Enable it either at build time (flip AGENT_ACTIVITY_DEMO_ENABLED) or at
 * runtime with `localStorage.setItem('agentActivityDemo', 'true')` (no rebuild).
 */
import type { AgentActivityEvent, ChatMessage } from '../useChatStore';
import { useChatStore } from '../useChatStore';

/** Build-time toggle — flip to `true` to always surface the preview button. */
export const AGENT_ACTIVITY_DEMO_ENABLED = false;

/** Runtime-overridable gate: the build flag OR the `agentActivityDemo` localStorage key. */
export const isAgentActivityDemoEnabled = (): boolean => {
  if (AGENT_ACTIVITY_DEMO_ENABLED) {
    return true;
  }
  try {
    return (
      typeof window !== 'undefined' &&
      window.localStorage.getItem('agentActivityDemo') === 'true'
    );
  } catch {
    return false;
  }
};

const iso = (base: number, offsetMs: number): string =>
  new Date(base + offsetMs).toISOString();

/** A rich, all-green turn: thinking, a timed tool, a grouped ×N run, a subagent. */
const successEvents = (t0: number): AgentActivityEvent[] => [
  {
    kind: 'thinking',
    text: 'The user wants a risk-posture summary. I should pull the dashboard totals first, then look at the top risks individually before writing anything.',
  },
  {
    kind: 'tool_start',
    toolUseId: 's-dash',
    name: 'mcp__risksmart__get_risk_dashboard',
    input: { window: 'last_90_days' },
    startedAt: iso(t0, 0),
  },
  {
    kind: 'tool_complete',
    toolUseId: 's-dash',
    isError: false,
    result: JSON.stringify(
      { total: 128, high: 12, overdueReviews: 5 },
      null,
      2
    ),
    endedAt: iso(t0, 1240),
  },
  // Grouped ×3 — three consecutive same-name calls collapse into one node.
  {
    kind: 'tool_start',
    toolUseId: 's-r1',
    name: 'mcp__risksmart__get_risk',
    input: { id: 'R-018' },
    startedAt: iso(t0, 1300),
  },
  {
    kind: 'tool_complete',
    toolUseId: 's-r1',
    isError: false,
    result: '{"id":"R-018","title":"Supplier concentration","rating":"High"}',
    endedAt: iso(t0, 1720),
  },
  {
    kind: 'tool_start',
    toolUseId: 's-r2',
    name: 'mcp__risksmart__get_risk',
    input: { id: 'R-042' },
    startedAt: iso(t0, 1740),
  },
  {
    kind: 'tool_complete',
    toolUseId: 's-r2',
    isError: false,
    result: '{"id":"R-042","title":"Data retention","rating":"High"}',
    endedAt: iso(t0, 2010),
  },
  {
    kind: 'tool_start',
    toolUseId: 's-r3',
    name: 'mcp__risksmart__get_risk',
    input: { id: 'R-077' },
    startedAt: iso(t0, 2030),
  },
  {
    kind: 'tool_complete',
    toolUseId: 's-r3',
    isError: false,
    result: '{"id":"R-077","title":"Access review backlog","rating":"Medium"}',
    endedAt: iso(t0, 2350),
  },
  { kind: 'thinking', text: '', redacted: true },
  // Subagent with its own nested step list.
  {
    kind: 'tool_start',
    toolUseId: 'task-1',
    name: 'Task',
    input: {
      subagent_type: 'controls-researcher',
      description: 'Check which controls mitigate the two High risks',
    },
    startedAt: iso(t0, 2400),
  },
  {
    kind: 'thinking',
    text: 'I need the controls linked to R-018 and R-042, then their latest test results.',
    parentToolUseId: 'task-1',
  },
  {
    kind: 'tool_start',
    toolUseId: 'sa-c1',
    name: 'mcp__risksmart__get_controls_for_risk',
    input: { riskId: 'R-018' },
    parentToolUseId: 'task-1',
    subagentType: 'controls-researcher',
    startedAt: iso(t0, 2500),
  },
  {
    kind: 'tool_complete',
    toolUseId: 'sa-c1',
    isError: false,
    result: '[{"id":"C-3","title":"Supplier due diligence"}]',
    endedAt: iso(t0, 3100),
  },
  {
    kind: 'tool_start',
    toolUseId: 'sa-c2',
    name: 'mcp__risksmart__get_control_test_results',
    input: { controlId: 'C-3' },
    parentToolUseId: 'task-1',
    subagentType: 'controls-researcher',
    startedAt: iso(t0, 3150),
  },
  {
    kind: 'tool_complete',
    toolUseId: 'sa-c2',
    isError: false,
    result: '{"lastResult":"Effective","testedOn":"2026-06-30"}',
    endedAt: iso(t0, 3900),
  },
  {
    kind: 'tool_complete',
    toolUseId: 'task-1',
    isError: false,
    result: 'Subagent finished.',
    endedAt: iso(t0, 3950),
  },
];

/** A turn that hit problems: an errored tool, a denied tool, a truncated result, a turn error. */
const problemEvents = (t0: number): AgentActivityEvent[] => [
  {
    kind: 'thinking',
    text: 'Let me try to fetch the full audit export and then update the risk record.',
  },
  {
    kind: 'tool_start',
    toolUseId: 'p-export',
    name: 'mcp__risksmart__export_audit_log',
    input: { format: 'json' },
    startedAt: iso(t0, 0),
  },
  {
    kind: 'tool_complete',
    toolUseId: 'p-export',
    isError: false,
    result: `${'{"event":"login","user":"…"}\n'.repeat(40)}`,
    resultTruncated: true,
    endedAt: iso(t0, 900),
  },
  {
    kind: 'tool_start',
    toolUseId: 'p-get',
    name: 'mcp__risksmart__get_risk',
    input: { id: 'R-999' },
    startedAt: iso(t0, 950),
  },
  {
    kind: 'tool_complete',
    toolUseId: 'p-get',
    isError: true,
    result: 'Risk R-999 not found.',
    endedAt: iso(t0, 1150),
  },
  {
    kind: 'tool_denied',
    toolUseId: 'p-del',
    name: 'mcp__risksmart__delete_risk',
    reasonCode: 'mode',
  },
  { kind: 'turn_error', code: 'error_max_turns' },
];

/** A live, in-flight turn: thinking done, a tool still running (animated spinner). */
const runningEvents = (t0: number): AgentActivityEvent[] => [
  {
    kind: 'thinking',
    text: 'I will query the open actions register and summarise what is overdue.',
  },
  {
    kind: 'tool_start',
    toolUseId: 'run-1',
    name: 'mcp__risksmart__list_actions',
    input: { status: 'open', overdue: true },
    startedAt: iso(t0, 0),
  },
];

const message = (
  id: string,
  content: string,
  activity: AgentActivityEvent[]
): ChatMessage => ({
  id,
  content,
  timestamp: new Date(),
  isUser: false,
  isSystem: false,
  metadata: { activity },
});

/**
 * Seed three demo assistant messages (successful, problematic, and a live
 * running one) and mark the running one as streaming so the animated spinner +
 * collapsed "Working…" line show. Clear the chat (or refresh) to remove them.
 */
export const seedAgentActivityDemo = (): void => {
  const now = Date.now();
  const runningId = 'demo-running';

  const messages: ChatMessage[] = [
    message(
      'demo-user-1',
      'Give me a summary of our current risk posture.',
      []
    ),
    message(
      'demo-success',
      "Here's your risk posture: 128 risks total, 12 High. The two most material High risks are supplier concentration (R-018) and data retention (R-042); both are covered by controls that last tested Effective.",
      successEvents(now)
    ),
    message(
      'demo-user-2',
      'Now delete risk R-999 and export the full audit log.',
      []
    ),
    message(
      'demo-problem',
      "I couldn't complete that. R-999 doesn't exist, deleting risks isn't permitted in read-only mode, and the turn hit its step limit.",
      problemEvents(now)
    ),
    message('demo-user-3', 'What actions are overdue?', []),
    message(runningId, '', runningEvents(now)),
  ];
  // The user messages carry no activity; give them the user styling.
  messages[0].isUser = true;
  messages[2].isUser = true;
  messages[4].isUser = true;

  useChatStore.setState({
    messages,
    chatMode: 'agent',
    streamingMessageId: runningId,
    isStreaming: true,
    isLoading: false,
    sessionId: 'demo-session',
  });
};

// Console fallback — only exposed when the preview harness is enabled, so the
// seeder is never reachable from a normal build. Run window.__seedAgentActivity()
// from devtools if the button isn't visible.
if (typeof window !== 'undefined' && isAgentActivityDemoEnabled()) {
  (
    window as unknown as { __seedAgentActivity?: () => void }
  ).__seedAgentActivity = seedAgentActivityDemo;
}
